'use client';

import { RollingCounter } from '@/components/girl/digit';
import { useLikes } from '@/components/likes/like.provider';
import { useLanguage } from '@bells/core/i18n';
import { getInitials } from '@bells/core/lib/initals';
import { cn } from '@bells/core/lib/utils';
import {
  attendantBlocked,
  attendantDateRequest,
  attendantLikesTitle,
  attendantMakeGift,
  attendantNoInfoYet,
  attendantRatingPunctuality,
  attendantRatingResponseTime,
  attendantsAge,
  attendantSendMessage,
  featureNotAvailable,
  homeNewcomer,
} from '@bells/i18n';
import languages from '@bells/iso/languages';
import {
  AttendantProfileState,
  AttendantSearchSingleResponse,
  MobilityStatus,
  VisitStatus,
} from '@bells/schema/models/attendant';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@bells/ui/components/avatar';
import { Badge } from '@bells/ui/components/badge';
import { Button } from '@bells/ui/components/button';
import { FlagIcon } from '@bells/ui/components/flag-icon';
import { Separator } from '@bells/ui/components/separator';
import { Skeleton } from '@bells/ui/components/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bells/ui/components/tooltip';
import { addMonths, isAfter } from 'date-fns';
import { first } from 'lodash';
import {
  Calendar,
  Gift,
  Heart,
  Loader2,
  MessageSquareText,
  Ruler,
  Zap,
} from 'lucide-react';
import { useMemo } from 'react';

export const GirlDesktopHeader = ({
  data,
  setOpen,
}: {
  data?: AttendantSearchSingleResponse;
  setOpen: (val: boolean) => void;
}) => {
  const isNew = useMemo(() => {
    if (!data) {
      return false;
    }
    if (!data.created_at) {
      return false;
    }
    const oneMonthAgo = addMonths(new Date(), -1);
    return isAfter(new Date(data.created_at), oneMonthAgo);
  }, [data]);

  const defaultImage = useMemo(() => {
    if (!data) {
      return false;
    }
    return (
      data.defaultMedia ??
      first(data.media?.filter((df) => df.type == 'image' && df.isVisible))
    );
  }, [data]);

  const { likes, doLike, isLoading, hasLike, disabled } = useLikes();
  const { language } = useLanguage();
  return (
    <div className='flex flex-col gap-2 p-4 pb-0 lg:p-0'>
      <div className='flex items-center lg:flex-col lg:items-start'>
        <Avatar className='mr-2 size-16 lg:mb-4 lg:size-32'>
          {data && (
            <AvatarFallback className='text-xl'>
              {getInitials(data.name)}
            </AvatarFallback>
          )}

          {defaultImage && (
            <AvatarImage
              className='object-cover'
              src={defaultImage.thumbnail}
            ></AvatarImage>
          )}
          {!data && (
            <Skeleton className='absolute left-0 top-0 h-full w-full'></Skeleton>
          )}
        </Avatar>
        <div className='flex w-full flex-col gap-1'>
          <h1 className='w-full scroll-m-20 gap-2 text-xs font-bold tracking-tight first:mt-0 md:text-xl lg:pb-2 lg:text-6xl'>
            {data ? data.name : <Skeleton className='h-6 w-full'></Skeleton>}
          </h1>
          <div className='flex w-full flex-wrap gap-2 text-sm lg:gap-4 lg:text-base'>
            {isNew && (
              <span className='break-keep bg-brand px-2 py-0 text-sm uppercase text-brand-foreground'>
                {homeNewcomer()}
              </span>
            )}
            {data ? (
              <>
                {data.locations?.map((df, index) => {
                  return (
                    <div key={`loc-${index}`}>
                      <FlagIcon
                        countryCode={df.countryCode.toUpperCase()}
                        alt={
                          languages[df.countryCode.toUpperCase()]?.names[
                            language
                          ]
                        }
                        className='mr-1 inline-block h-4 w-4'
                      ></FlagIcon>
                      {df.name}
                    </div>
                  );
                })}
                {data.height && (
                  <span className='whitespace-nowrap break-keep'>
                    <Ruler className='mr-1 inline-block h-4 w-4'></Ruler>
                    {`${data.height} cm`}
                  </span>
                )}
                {data.age && (
                  <span className='whitespace-nowrap break-keep'>
                    <Calendar className='mr-1 inline-block h-4 w-4'></Calendar>
                    {attendantsAge({ age: data.age })}
                  </span>
                )}
              </>
            ) : (
              <Skeleton className='h-6 w-full'></Skeleton>
            )}
          </div>
        </div>
      </div>

      <div className='mb-2 flex flex-col gap-2 text-xs text-muted-foreground md:text-[14px] md:leading-5'>
        {data ? (
          <p className='line-clamp-3'>{data.short_biopic}</p>
        ) : (
          <>
            <Skeleton className='h-5 w-full'></Skeleton>
            <Skeleton className='h-5 w-full'></Skeleton>
            <Skeleton className='h-5 w-full'></Skeleton>
          </>
        )}
      </div>

      <div className='hidden flex-wrap items-center justify-between gap-4'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='flex flex-col gap-1'>
              <span className='text-base font-medium'>
                {attendantRatingPunctuality()}
              </span>
              <span>{attendantNoInfoYet()}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>{featureNotAvailable()}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div className='flex flex-col gap-1'>
              <span className='text-base font-medium'>
                {attendantRatingResponseTime()}
              </span>
              <div>
                <Badge className='gap-1'>
                  <Zap className='h-4 w-4'></Zap>
                  {attendantNoInfoYet()}
                </Badge>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>{featureNotAvailable()}</TooltipContent>
        </Tooltip>
      </div>

      <div className='flex items-center'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='w-full'>
              <Button
                disabled={
                  !data || data.state !== AttendantProfileState.APPROVED
                }
                className='w-full rounded-r-none lg:rounded-r-md'
                onClick={() => setOpen(true)}
              >
                <Calendar></Calendar>
                {attendantDateRequest()}
              </Button>
            </div>
          </TooltipTrigger>
          {data && data.state !== AttendantProfileState.APPROVED && (
            <TooltipContent>{attendantBlocked()}</TooltipContent>
          )}
        </Tooltip>

        <Button
          onClick={doLike}
          disabled={isLoading || !data || disabled}
          variant={'outline'}
          className='gap-1 rounded-l-none lg:hidden'
        >
          {isLoading ? (
            <Loader2 className={cn('h-4 w-4 animate-spin', 'mr-2')} />
          ) : (
            <Heart
              className={cn(
                hasLike ? 'fill-pink-500 text-pink-500' : 'text-pink-500'
              )}
            ></Heart>
          )}

          <RollingCounter
            hasShadow={false}
            value={likes}
            digitSize={16}
          ></RollingCounter>
        </Button>
      </div>

      <Separator className='mb-4 mt-4 hidden w-full lg:block'></Separator>
      <Button
        onClick={doLike}
        disabled={isLoading || !data}
        variant={'outline'}
        className='hidden w-full lg:flex'
      >
        {isLoading ? (
          <Loader2 className={cn('h-4 w-4 animate-spin', 'mr-2')} />
        ) : (
          <Heart
            className={cn(
              hasLike ? 'fill-pink-500 text-pink-500' : 'text-pink-500'
            )}
          ></Heart>
        )}

        <RollingCounter
          hasShadow={false}
          value={likes}
          digitSize={16}
        ></RollingCounter>
        <div className='hidden flex-col items-start lg:flex'>
          <span className='font-medium'>{attendantLikesTitle()}</span>
        </div>
      </Button>
      <div className='hidden w-full lg:block'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='w-full'>
              <Button
                disabled
                variant={'outline'}
                size={'lg'}
                className='w-full'
              >
                <MessageSquareText></MessageSquareText>
                <span>{attendantSendMessage()}</span>
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>{featureNotAvailable()}</TooltipContent>
        </Tooltip>
      </div>
      <div className='hidden w-full lg:block'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='w-full'>
              <Button
                disabled
                variant={'outline'}
                size={'lg'}
                className='w-full'
              >
                <Gift></Gift>
                <span>{attendantMakeGift()}</span>
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>{featureNotAvailable()}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
